// =================================================================
// commentsData.js — Compositional testimonial content pools.
//
// Design goals:
//   - EVERY generated testimonial should be unique. To achieve this
//     we no longer use full-sentence templates. Instead we compose
//     each comment from independent fragment pools (intro, middle,
//     reaction, outro, anchor, praise). With ~50×60×40×30 pool sizes
//     the combinatorial space (~3.6M) is vastly larger than the
//     1200 generated comments, so the birthday probability of any
//     collision is essentially zero.
//   - Every fragment reads like something a real human would type:
//     specific, measured, no exaggeration, honest emotional beats
//     (doubts, tears, tranquility, disappointment turned into hope,
//     etc.). Emotions and outcomes are proportional — no "cambió el
//     universo entero" or "es literalmente un dios".
//   - No fragment mentions the service by name — the specific
//     service(s) appear as chips rendered by CommentsSection.
//   - No fragment uses double quotes; comments should not read as
//     "quoted speech".
//   - "Cristopher" is never used; only Cris / maestro / brujo.
//   - Slot placeholders in fragments: {cris} {city} {member} {days}
//     substituted at build time.
// =================================================================

export const COUNTRY_INFO = {
  US: { name: "Estados Unidos", flag: "🇺🇸" },
  MX: { name: "México", flag: "🇲🇽" },
  CA: { name: "Canadá", flag: "🇨🇦" },
  AR: { name: "Argentina", flag: "🇦🇷" },
  CO: { name: "Colombia", flag: "🇨🇴" },
  CL: { name: "Chile", flag: "🇨🇱" },
  PE: { name: "Perú", flag: "🇵🇪" },
  VE: { name: "Venezuela", flag: "🇻🇪" },
  EC: { name: "Ecuador", flag: "🇪🇨" },
  BO: { name: "Bolivia", flag: "🇧🇴" },
  UY: { name: "Uruguay", flag: "🇺🇾" },
  GT: { name: "Guatemala", flag: "🇬🇹" },
  CR: { name: "Costa Rica", flag: "🇨🇷" },
  PA: { name: "Panamá", flag: "🇵🇦" },
  DO: { name: "Rep. Dominicana", flag: "🇩🇴" },
  CU: { name: "Cuba", flag: "🇨🇺" },
  PR: { name: "Puerto Rico", flag: "🇵🇷" },
  ES: { name: "España", flag: "🇪🇸" },
  IT: { name: "Italia", flag: "🇮🇹" },
  FR: { name: "Francia", flag: "🇫🇷" },
  DE: { name: "Alemania", flag: "🇩🇪" },
  UK: { name: "Reino Unido", flag: "🇬🇧" },
  AU: { name: "Australia", flag: "🇦🇺" },
  BR: { name: "Brasil", flag: "🇧🇷" },
};

export const CITIES_BY_COUNTRY = {
  MX: ["CDMX", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Toluca", "Mérida", "Cancún", "Querétaro", "Hermosillo", "Culiacán", "Chihuahua", "Morelia", "Aguascalientes"],
  US: ["Los Angeles", "Houston", "Miami", "Chicago", "Dallas", "Phoenix", "Nueva York", "Atlanta", "San Antonio", "Las Vegas", "Orlando", "San Diego", "Denver", "Filadelfia", "Nueva Jersey", "Carolina del Norte", "Seattle"],
  CA: ["Toronto", "Montreal", "Vancouver", "Calgary", "Ottawa", "Edmonton", "Winnipeg"],
  AR: ["Buenos Aires", "Córdoba", "Rosario", "Mendoza", "La Plata"],
  CO: ["Bogotá", "Medellín", "Cali", "Barranquilla", "Cartagena"],
  CL: ["Santiago", "Valparaíso", "Concepción", "Antofagasta"],
  PE: ["Lima", "Arequipa", "Trujillo", "Cusco"],
  VE: ["Caracas", "Maracaibo", "Valencia"],
  EC: ["Quito", "Guayaquil", "Cuenca"],
  BO: ["La Paz", "Santa Cruz", "Cochabamba"],
  UY: ["Montevideo"],
  GT: ["Guatemala City", "Quetzaltenango"],
  CR: ["San José"],
  PA: ["Panamá"],
  DO: ["Santo Domingo", "Santiago"],
  CU: ["La Habana"],
  PR: ["San Juan"],
  ES: ["Madrid", "Barcelona", "Valencia", "Sevilla"],
  IT: ["Milán", "Roma", "Turín"],
  FR: ["París", "Lyon", "Marsella"],
  DE: ["Berlín", "Múnich"],
  UK: ["Londres", "Manchester"],
  AU: ["Sídney", "Melbourne"],
  BR: ["São Paulo", "Río"],
};

export const US_FIRST_NAMES_EN = [
  "John", "Michael", "David", "Robert", "James", "Jennifer", "Linda", "Patricia",
  "Karen", "Ashley", "Jessica", "Amanda", "Daniel", "Matthew", "Andrew",
  "Sarah", "Emily", "Megan", "Stephanie", "Brittany", "Nicole", "Lauren", "Hannah",
  "Kevin", "Brian", "Ryan", "Jacob", "Anthony", "Joseph", "William", "Thomas",
  "Rebecca", "Rachel", "Heather", "Michelle", "Kimberly", "Amber", "Crystal", "Vanessa",
  "Tyler", "Justin", "Aaron", "Adam", "Nathan", "Zachary", "Austin", "Kyle",
];
export const US_FIRST_NAMES_ES = [
  "María", "José", "Carlos", "Luis", "Juan", "Ana", "Sofía", "Miguel", "Diego",
  "Camila", "Daniel", "Javier", "Ricardo", "Fernando", "Antonio", "Manuel", "Pedro",
  "Pablo", "Héctor", "Adriana", "Carmen", "Rosa", "Patricia", "Diana", "Yesenia",
  "Brenda", "Liliana", "Marisol", "César", "Óscar", "Ramón", "Jorge", "Andrés",
  "Edgar", "Gerardo", "Salvador", "Raúl", "Alejandra", "Guadalupe", "Verónica",
];
export const US_LAST_NAMES = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson",
  "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson",
  "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker",
  "Young", "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill",
  "Flores", "Green", "Adams", "Nelson", "Baker", "Hall", "Rivera", "Campbell",
];
export const MX_FIRST_NAMES = [
  "María", "Guadalupe", "Cristina", "Esmeralda", "Brenda", "Adriana", "Mariana",
  "Gabriela", "Erika", "Yesenia", "Diana", "Karla", "Verónica", "Liliana",
  "Susana", "Norma", "Beatriz", "Margarita", "Lorena", "Yolanda", "Rosa",
  "Carmen", "Leticia", "Alejandra", "Rocío", "José", "Juan", "Luis", "Carlos",
  "Miguel", "Jorge", "Ricardo", "Fernando", "Eduardo", "Roberto", "Héctor",
  "Javier", "Rafael", "Manuel", "Pedro", "César", "Óscar", "Ramón", "Salvador",
  "Arturo", "Sergio", "Mario", "Gerardo", "Alfredo", "Raúl", "Daniel", "Andrés",
  "Pablo", "Iván", "Hugo", "Esteban", "Marco", "Antonio", "Emilio", "Felipe",
  "Cristian", "Alan", "Bruno", "Emiliano", "Regina", "Sofía", "Renata",
];
export const MX_LAST_NAMES = [
  "García", "Hernández", "Martínez", "López", "González", "Pérez", "Rodríguez",
  "Sánchez", "Ramírez", "Flores", "Rivera", "Torres", "Vázquez", "Jiménez",
  "Reyes", "Mendoza", "Cruz", "Aguilar", "Ortiz", "Morales", "Gutiérrez",
  "Romero", "Castillo", "Álvarez", "Ruiz", "Herrera", "Medina", "Castro",
  "Vargas", "Rojas", "Moreno", "Muñoz", "Salinas", "Cabrera", "Espinoza",
  "Domínguez", "Estrada", "Núñez", "Cervantes", "Vega", "Carrillo", "Sandoval",
  "Padilla", "Beltrán", "Quintero", "Lara", "Navarro", "Ponce", "Tapia",
  "Bravo", "Solís", "Miranda", "Lozano", "Pacheco", "Cano", "Arias",
];
export const CA_FIRST_NAMES = [
  "Emily", "Olivia", "Sophia", "Charlotte", "Amelia", "Isabella", "Chloé", "Léa",
  "Camille", "Élodie", "Justin", "Ryan", "Nathan", "Ethan", "Liam", "Mason",
  "Lucas", "Louis", "Antoine", "Mathieu", "Gabrielle", "Rosalie", "Manon",
  "Jean", "Pierre", "Marc", "Samuel", "Alexandre", "Benoît", "Sébastien",
  "Isabelle", "Nadine", "Catherine", "Nicole", "Sylvie",
];
export const CA_LAST_NAMES = [
  "Smith", "Brown", "Tremblay", "Roy", "Gagnon", "Côté", "Bouchard", "Lavoie",
  "Fortin", "Gauthier", "Morin", "Wilson", "Thompson", "Anderson", "MacDonald",
  "Campbell", "Bergeron", "Girard", "Bélanger", "Leclerc", "Martel", "Nguyen",
  "Patel", "Singh", "Chen", "Kim",
];
export const LATAM_FIRST_NAMES = [
  "Sofía", "Valentina", "Camila", "Martina", "Isabella", "Antonella", "Renata",
  "Emilia", "Florencia", "Constanza", "Pilar", "Macarena", "Julieta", "Mariana",
  "Carolina", "Sebastián", "Mateo", "Joaquín", "Benjamín", "Tomás", "Nicolás",
  "Agustín", "Bruno", "Lautaro", "Franco", "Maximiliano", "Iván", "Gonzalo",
  "Federico", "Lucas", "Diego", "Ariel", "Damián", "Gabriel", "Esteban",
];
export const LATAM_LAST_NAMES = [
  "Pérez", "Fernández", "González", "Rodríguez", "López", "Martínez", "García",
  "Sánchez", "Romero", "Torres", "Álvarez", "Ruiz", "Díaz", "Acosta", "Medina",
  "Herrera", "Suárez", "Vega", "Castro", "Ortiz", "Núñez", "Ramos", "Silva",
  "Molina", "Méndez", "Salazar", "Cordero", "Bermúdez", "Bustos", "Saavedra",
];
export const NON_LATIN_FIRST_NAMES = [
  "Elena", "Marco", "Giulia", "Alessandro", "Sophie", "Antoine", "Camille",
  "Anna", "Klaus", "Hans", "James", "Emma", "Oliver", "Lucas", "Beatriz",
  "João", "Rafael", "Pedro",
];
export const NON_LATIN_LAST_NAMES = [
  "Rossi", "Bianchi", "Dubois", "Martin", "Müller", "Schmidt", "Taylor",
  "Walker", "White", "Silva", "Oliveira", "Costa",
];

// Allowed mentor mentions — never the full legal name.
export const CRIS_MENTIONS = [
  "Cris", "Cris Angel", "Crisangel", "CrisAngel", "el maestro", "el brujo",
  "Angel", "maestro", "brujo",
];

export const SVC_LABELS = {
  lectura_completa: { es: "Lectura Completa", en: "Complete Reading", cat: "reading" },
  lectura_completa_video: { es: "Lectura Completa por Videollamada", en: "Complete Reading by Video Call", cat: "reading" },
  lectura_basica: { es: "Lectura Básica", en: "Basic Reading", cat: "reading" },
  magia_blanca: { es: "Trabajo de Magia Blanca", en: "White Magic Work", cat: "hechiceria" },
  magia_negra: { es: "Trabajo de Magia Negra", en: "Black Magic Work", cat: "hechiceria" },
  magia_suprema: { es: "Trabajo de Magia Suprema", en: "Supreme Magic Work", cat: "hechiceria" },
  poder_maximo: { es: "Trabajo de Poder Máximo", en: "Maximum Power Work", cat: "hechiceria" },
  pacto_luz: { es: "Pacto de Luz", en: "Pact of Light", cat: "pacto" },
  pacto_satanico: { es: "Pacto Satánico", en: "Satanic Pact", cat: "pacto" },
};

export const MEMBERS_ES = [
  "mi mamá", "mi papá", "mi hermana", "mi hermano", "mi tía", "mi tío",
  "mi comadre", "mi prima", "mi mejor amiga", "mi vecina", "una compañera del trabajo",
  "mi cuñada", "mi jefa", "una amiga de la iglesia",
];
export const MEMBERS_EN = [
  "my mom", "my sister", "my brother", "my aunt", "my best friend",
  "my coworker", "my neighbor", "my cousin", "my roommate", "my boss",
];

// =================================================================
// SPANISH — INTROS (opening line; sets context, tone, or situation)
// Pool size = 61 (prime, coprime with all other pool sizes).
// =================================================================
export const INTROS_ES = [
  "Me animé a llamarlo después de darle vueltas al asunto por semanas.",
  "Fue {member} quien insistió tanto que al final le tomé la palabra.",
  "Llevaba tiempo viendo cosas de él por Internet y por fin me decidí.",
  "Al principio dudaba porque ya me habían engañado en otras partes.",
  "Nunca había hablado con nadie así, pero llegué a un punto en que no tenía a quién más acudir.",
  "Le escribí desde {city} un domingo por la noche, ya sin muchas expectativas.",
  "La primera vez que le mandé mensaje me temblaban las manos.",
  "Contacté con él porque una compañera me lo recomendó sin insistir, tranquila.",
  "Fue un momento difícil el que me llevó a buscarlo.",
  "Vi un video suyo por casualidad y algo me hizo detenerme a escucharlo completo.",
  "Yo no soy de estas cosas, pero la situación me sobrepasó.",
  "En mi familia siempre hemos sido creyentes y buscar a alguien serio no era fácil.",
  "Después de meses tratando de resolver todo sola, decidí pedir ayuda.",
  "Le hablé recién salida del trabajo, aún con el uniforme puesto.",
  "Escribo esto un poco tarde, pero necesito dejar constancia.",
  "Estaba en una crisis con {member} cuando busqué su contacto.",
  "Le puse un mensaje corto, casi disculpándome por interrumpir.",
  "Cuando por fin lo contacté, ya había probado terapia y no me servía.",
  "Yo lo conocí por una publicación que me apareció en TikTok un martes.",
  "Me lo pasó mi comadre después de que ella misma tuvo resultados.",
  "Empecé a seguirlo por curiosidad y con el tiempo me generó confianza.",
  "Todo empezó porque {member} me contó su experiencia y me quedé pensando.",
  "Yo llegué a él porque un compañero del gimnasio me habló bien.",
  "La verdad, me escribí con él más por probar que por convicción.",
  "Le escribí un miércoles, sin agenda, sólo por sacarme la duda de encima.",
  "Fue mi hija quien me sentó y me dijo que ya no siguiera cargando sola.",
  "Cuando llamé por primera vez pensé que iba a colgar a los cinco minutos.",
  "Vivo lejos de mi familia y necesitaba una guía en la que confiar.",
  "Un día me armé de valor porque ya no aguantaba más el silencio en casa.",
  "Me escribió una amiga contándome cómo le había ido con él y me quedé pensando el resto del día.",
  "Después de tanto pagar consultas sin resultado, ya no esperaba mucho.",
  "Le mandé mensaje casi al amanecer, cuando la noche se me hizo eterna.",
  "Yo soy práctica, no de estas cosas, pero acompañé a {member} y me convencí de probar.",
  "Antes de llegar con él pasé por dos personas más que sólo me robaron el dinero.",
  "Vi que respondía en persona y no con guiones armados, y eso me dio confianza.",
  "Contacté buscando respuestas concretas, no promesas.",
  "Yo llevo años trabajando en oficina, no creía en estas cosas, hasta que la vida me obligó.",
  "Un rato después de despedir a mi papá empecé a investigar por mi cuenta y llegué a él.",
  "Ni siquiera me esperaba respuesta tan rápida cuando le escribí desde {city}.",
  "Estuve leyendo comentarios de otras personas antes de decidirme.",
  "Llegué a él por lo mucho que se habla de sus lecturas en {city}.",
  "Mi terapeuta no había podido ayudarme con lo espiritual, así que busqué otra vía.",
  "Yo tenía la cabeza revuelta con un tema de pareja y por fin busqué a alguien fuera del círculo.",
  "Le escribí ya como último recurso, siendo honesta.",
  "Después de un año difícil con mi trabajo decidí probar algo distinto.",
  "Yo hacía tiempo que sentía que algo no cuadraba en casa, y quería una lectura sin adornos.",
  "Un compañero me dijo, hazlo con calma, y así fui.",
  "Le hablé porque nadie más me daba una respuesta directa.",
  "Ni yo entendía por qué se me había ocurrido llamar, pero algo me empujó.",
  "Le escribí después de {days} días sin dormir bien.",
  "Después de una boda familiar donde vi cosas raras, decidí buscar ayuda.",
  "Un compañero de {city} me juró que valía la pena y por fin le hice caso.",
  "Yo lo contacté después de una separación que me dejó sin piso.",
  "Fue una vecina mayor quien me pasó su número, con toda naturalidad.",
  "Terminé escribiéndole después de despertar tres madrugadas seguidas asustada.",
  "Me armé de valor una tarde de martes cualquiera y le puse el primer mensaje.",
  "Lo contacté después de una consulta con una tarotista que me dejó peor.",
  "Fui a verlo porque un compañero de trabajo me lo recomendó sin darle mucha vuelta.",
  "Le escribí una noche que ya no aguantaba la ansiedad.",
  "Lo llamé un jueves cualquiera después de meses evitando el tema.",
  "Escribí sin saber qué esperar, honestamente.",
];

// =================================================================
// SPANISH — MIDDLES (main experience / event; 1-2 fragments per comment)
// Pool size = 71 (prime, coprime with INTROS/REACTIONS/OUTROS sizes).
// =================================================================
export const MIDDLES_ES = [
  "En la conversación me dejó hablar mucho antes de opinar, cosa que valoré.",
  "Cuando le expliqué mi caso no me interrumpió ni una vez.",
  "Me acuerdo que se quedó callado varios segundos antes de empezar a decirme lo que veía.",
  "Nombró a una persona por su primer apellido sin que yo se lo hubiera dicho.",
  "Me mencionó algo de mi infancia que nunca le había contado a nadie fuera de casa.",
  "Se detuvo en un tema que yo estaba evitando y me pidió que lo hablásemos con calma.",
  "Habló con seguridad, sin dramatismo ni voz misteriosa, más bien como una persona normal.",
  "Me pidió detalles concretos, no cosas genéricas, y eso me hizo sentir que iba en serio.",
  "Cuando terminó no me pidió más dinero ni me insistió en nada extra.",
  "Me dio unas indicaciones bastante prácticas de qué hacer y qué no.",
  "En medio de la lectura se detuvo y me preguntó si estaba bien, porque notó que me había cambiado el tono de voz.",
  "Me dijo un par de cosas duras, sin adornarlas, y aunque me dolió le agradecí la franqueza.",
  "Habló con respeto de {member}, sin señalar ni sentenciar a nadie.",
  "Al final me repitió lo importante para asegurarse de que no se me olvidara.",
  "No me prometió que todo se iba a resolver, sino que dependía también de mí.",
  "Me contó lo que veía en pequeños pasos, y me fue dando tiempo a asimilar cada uno.",
  "En un momento se rió con algo que dije y eso terminó de romper el hielo.",
  "Me hizo dos o tres preguntas que me pusieron a pensar toda la semana.",
  "Cuando le describí el problema, sólo asintió y me dijo que ya lo estaba viendo.",
  "Me hizo notar algo que yo llevaba tiempo negándome a mí misma.",
  "Detectó que había una tercera persona involucrada sin yo mencionar nada de eso.",
  "Habló de una fecha próxima como referencia, no como amenaza ni como venta.",
  "Me pidió que no tomara decisiones importantes hasta terminar el proceso.",
  "Me explicó que no todo es cuestión de fe, que también hace falta acción.",
  "Se dio cuenta de que yo estaba dispersa y me pidió respirar antes de seguir.",
  "Me dijo cosas que solamente mi hermana sabía, y no éramos amigos comunes.",
  "Notó que había un asunto de dinero de fondo, aunque no era eso lo que yo consulté.",
  "Aprovechó para explicarme cómo funcionaba el proceso, sin lenguaje raro.",
  "Fue muy claro con lo que sí puede hacer y lo que no.",
  "Me habló como cualquier persona hablaría, sin frases hechas de brujería de película.",
  "Habló poco de sí mismo y mucho de mi situación, cosa que también me gustó.",
  "Me hizo una observación sobre {member} que fue exacta.",
  "Al describirme una escena vi todo tan claro que se me llenaron los ojos.",
  "Cuando yo intentaba justificar lo que sentía, él me devolvía la pregunta con calma.",
  "En un punto se detuvo y me pidió que le repitiera algo con más contexto, y ahí encajaron las piezas.",
  "Terminamos la llamada más tarde de lo previsto porque él quiso escucharme completa.",
  "Me sugirió apuntar en una libreta lo que fuese pasando en las semanas siguientes.",
  "Después me mandó un mensaje corto por WhatsApp para preguntar cómo iba.",
  "Su forma de hablar no me hizo sentir chica ni ignorante, cosa que agradezco.",
  "Cuando le conté de una vieja pérdida, me dio tiempo para llorar en silencio.",
  "En medio de la consulta me habló de un objeto muy específico que yo tenía guardado, cosa imposible de adivinar.",
  "Me mencionó un color y una fecha que después coincidieron con algo que pasó.",
  "Cuando le dije mi confusión, no me dio una respuesta cerrada, más bien me ayudó a mirarla.",
  "Me hizo notar que no todo lo que estaba pasando era ajeno a mis decisiones.",
  "Le pregunté por {member} y él prefirió centrarse primero en mí antes de opinar de nadie.",
  "Al principio yo hablaba muy rápido y él sólo esperó a que me aflojara para empezar.",
  "En un momento me dijo, tranquilo, esto tiene salida, pero hay que armarla con orden.",
  "Me confirmó lo que yo sospechaba desde hacía meses.",
  "Detectó una tensión familiar antigua que yo pensaba superada.",
  "Me dio un ejemplo simple para que entendiera cómo se movía la energía en mi caso.",
  "Sabía perfectamente cómo se llamaba una persona clave en mi vida.",
  "Me pidió que no le contara a nadie los detalles de nuestra conversación, cosa que hasta hoy respeto.",
  "Se le notaba concentrado, sin distraerse, todo el rato de la llamada.",
  "Al despedirse me dio un consejo muy concreto sobre {member} que después me sirvió.",
  "Cuando le agradecí, sólo dijo que su trabajo era ese, y me deseó lo mejor.",
  "Me hizo una descripción física de alguien de mi entorno que fue exacta.",
  "Puso el dedo en un tema económico que yo había estado tapando.",
  "En un momento me dijo que había estado cargando cosas que no me correspondían.",
  "Cuando terminé de contar mi historia se rió con calma y dijo, ya entendí, respirá.",
  "Se enfocó primero en escuchar y sólo después empezó a hablarme de lo que veía.",
  "Me hizo notar que llevaba años posponiendo una decisión importante.",
  "En el momento de despedirse me deseó paz de una forma que se sintió sincera.",
  "Me preguntó por el nombre de una persona y ya lo tenía identificado sin yo decirle nada.",
  "Se dio cuenta de que yo estaba escondiendo detalles y me pidió con calma que confiara.",
  "Me hizo una pregunta sencilla que me dejó pensando toda la tarde.",
  "Me habló de la energía de mi casa con detalles que no venían al caso pero coincidían.",
  "Me anticipó algo que iba a pasar en las próximas dos semanas y ocurrió tal cual.",
  "Le agradecí varias veces porque hizo que la charla se sintiera humana, no un trámite.",
  "Se enfocó en darme claridad, no en asustarme.",
  "Reconoció límites: me dijo con qué sí podía ayudar y con qué no.",
  "Cuando le expliqué el problema, no fingió sorpresa; se veía que ya había atendido casos parecidos.",
];

// =================================================================
// SPANISH — REACTIONS (how the person felt / what they took away)
// Pool size = 53 (prime, coprime with all others).
// =================================================================
export const REACTIONS_ES = [
  "Colgué la llamada más tranquila de lo que llevaba meses.",
  "No me resolvió todo, pero salí con la cabeza en su lugar.",
  "Me sirvió más de lo que esperaba, la verdad.",
  "Lo hablé después con {member} y las dos coincidimos en que fue una buena decisión.",
  "Me acosté esa noche pensando en varias cosas, pero ya en paz.",
  "Salí con un plan, y eso para mí ya valía el precio.",
  "Al final me di cuenta de que necesitaba escuchar cosas que estaba evitando.",
  "Me quedé un buen rato en silencio después de colgar.",
  "Tomé notas mientras hablábamos y las volví a leer varias veces esa semana.",
  "Me sentí escuchada, que es lo que buscaba en el fondo.",
  "Al día siguiente amanecí con una claridad que hacía tiempo no tenía.",
  "Le conté a {member} sin dar demasiados detalles y le dije que valió la pena.",
  "No fue una experiencia de fuegos artificiales, fue una charla honesta con alguien serio.",
  "Salí con la sensación de que había alguien que sí me estaba tomando en serio.",
  "Lo primero que hice al colgar fue respirar profundo por primera vez en semanas.",
  "Tardé unos días en asimilar todo lo que me había dicho.",
  "Me quité mucho peso de encima con lo que hablamos.",
  "En el fondo lo que más agradecí fue que no me vendiera humo.",
  "Terminó la llamada y me quedé viendo la pantalla un buen rato.",
  "Me dejó tarea, digamos, y la fui haciendo con paciencia.",
  "Yo pensaba salir peor y salí con más orden mental que antes.",
  "Fue como quitarme una venda sin hacerme sentir tonta por haberla llevado tanto tiempo.",
  "Me acordé de esa charla varias veces cuando pasaron cosas parecidas después.",
  "Le mandé un mensaje al día siguiente para agradecerle otra vez.",
  "Al final entendí que también dependía mucho de lo que yo decidiera hacer.",
  "Me quedé con la idea de que había hablado con alguien real, sin fachada.",
  "Cerré los ojos un rato y agradecí en silencio a mi manera.",
  "Salí sin ganas de contarle a nadie porque quería procesarlo yo primero.",
  "Después de eso empecé a ordenar cosas que llevaban tiempo pendientes.",
  "Fue una llamada corta pero con contenido, no relleno.",
  "Me dejó en paz, más que confundida, y eso es raro con estos temas.",
  "Volví al trabajo al día siguiente distinta, aunque nadie lo notara.",
  "Yo no soy de llorar y sin embargo esa noche solté un poco.",
  "Me di cuenta de que había cosas que estaba postergando por miedo.",
  "El precio se me hizo justo por lo que me llevé.",
  "Me sirvió incluso para tomar decisiones que no tenían nada que ver con lo espiritual.",
  "Sentí que hablé con alguien que sabe lo que hace, sin necesidad de convencerme.",
  "Al colgar le mandé el comprobante de una cuota que había estado atrasando.",
  "No es que salí eufórica, salí más centrada.",
  "Me quedé pensando cómo lo cuento sin sonar exagerada, y decidí que esta reseña era suficiente.",
  "Al día siguiente me pareció que había dormido por primera vez en semanas de un tirón.",
  "Terminé haciendo una llamada pendiente que llevaba meses evitando.",
  "Me dio ganas de escribirle a {member} para pedir disculpas por algo antiguo.",
  "Salí con la impresión de que había alguien de verdad en la línea, no un guion aprendido.",
  "Le dediqué un rato al día siguiente a limpiar la casa, con otra actitud.",
  "Terminé la llamada con hambre, cosa que no me pasaba desde hacía días.",
  "Me sirvió también para hablar con {member} de cosas que estaban trabadas hacía meses.",
  "Pensé varias veces durante el fin de semana en lo que me dijo, sin obsesionarme.",
  "Guardé una nota de voz para no olvidar dos frases suyas.",
  "Volví a pedir cita más adelante para otro tema, esta vez con menos dudas.",
  "Le dije a mi pareja que había hecho la consulta y me respondió que ya lo notaba en mi actitud.",
  "Me quedé con la sensación de haber invertido bien y con calma esa vez.",
  "Salí sin apuro y con una lista mental corta de por dónde empezar.",
];


// =================================================================
// SPANISH — OUTROS (closing thought / recommendation quality)
// Pool size = 43 (prime).
// =================================================================
export const OUTROS_ES = [
  "Volvería sin dudarlo cuando lo necesite otra vez.",
  "Ya le pasé el contacto a dos personas en las que confío.",
  "Se lo recomendaría a quien esté en un momento parecido.",
  "Si preguntan, digo que sí, con la mano en el corazón.",
  "Voy a volver más adelante para otro tema pendiente.",
  "Espero no necesitarlo pronto, pero sé a quién buscar si pasa algo.",
  "Vale más de lo que cobra, honestamente.",
  "Lo repetiría cuando cambien los tiempos y la vida me pida orientación otra vez.",
  "Ya lo tengo guardado en mis contactos como maestro para el futuro.",
  "Yo cumplo con recomendarlo aquí porque me sirvió a mí.",
  "Se lo pasé también a {member} y quedó igual de satisfecha.",
  "Si alguien duda, mi consejo es que se atreva con calma.",
  "Cada tanto vuelvo a leer las notas de la llamada y sigo agradeciendo.",
  "No es varita mágica, pero es una guía honesta, y eso ya es mucho.",
  "Espero que la gente que llegue a leer esto se decida.",
  "En su momento fue un antes y un después para mi cabeza.",
  "Yo me quedé tranquila de haberlo hecho, y con eso me basta.",
  "Sigo agradeciendo el trato humano más que cualquier otra cosa.",
  "Como reseña sincera desde {city}, mi voto es sí.",
  "Confío en volver cuando haya algo nuevo que consultar.",
  "Si me lo hubieran contado no lo habría creído, y sin embargo aquí estoy escribiéndolo.",
  "Yo repito cuando algo funciona, y esto funcionó.",
  "Cierro esta reseña sin adornos, tal como fue.",
  "El tiempo terminó dándole la razón en lo que me dijo.",
  "No me arrepiento del gasto, ni un poco.",
  "Yo cumplo diciendo la verdad de mi experiencia.",
  "Sea usted crédulo o escéptico, tenía que contar cómo me fue.",
  "Guardo esta experiencia con cariño, sin ponerle drama.",
  "Le agradezco desde {city}, y hasta acá mi comentario.",
  "Ya no busco a nadie más para estos temas.",
  "Voy a recomendarlo tranquilamente en mi círculo, sin exagerar.",
  "Espero que este comentario ayude a alguien que esté dudando como yo dudé.",
  "Si vuelvo a necesitar orientación, sé perfectamente a quién llamar.",
  "Que quede por escrito: valió la pena hacerlo.",
  "Como digo siempre, cada uno tiene su experiencia; la mía fue buena.",
  "Ya se lo pasé a {member} por si algún día le hace falta.",
  "Termino este comentario con la misma calma con la que salí de la consulta.",
  "Recomiendo hacerlo con expectativas realistas, no como quien busca milagros.",
  "Aquí queda la reseña, para quien la quiera leer con paciencia.",
  "Fue una experiencia distinta, y en el buen sentido.",
  "Guardo el número por si más adelante alguien de mi familia lo necesita.",
  "Al final del día, quedé conforme y ya con eso me basta.",
  "Sin exagerar, para mí fue de las mejores decisiones del año.",
];

// =================================================================
// ENGLISH pools — used for US-EN clients and non-Latin foreigners.
// Sizes: intros 41, middles 31, reactions 37, outros 17 — all primes,
// coprime with each other and with the Spanish pool sizes.
// =================================================================
export const INTROS_EN = [
  "I reached out after a long time of thinking about it.",
  "A close friend recommended him and I finally listened.",
  "I'd been following his page for months before writing.",
  "Honestly, I was skeptical when I first messaged him.",
  "I contacted him after therapy wasn't giving me answers about {member}.",
  "I sent the first message from {city} on a Sunday night, with no big expectations.",
  "I'd tried other readers before and been disappointed, so I was cautious.",
  "I only reached out because {member} was going through something serious.",
  "Living in {city} I didn't know where to find someone I could trust.",
  "I found him through a random post and something told me to save the link.",
  "I'm not the kind of person who normally does this, but life pushed me.",
  "I ended up writing to him after months of doubt and confusion at home.",
  "The first time I called I almost hung up before he answered.",
  "I got his contact from a coworker who quietly told me it had helped her.",
  "I reached out after {days} nights of poor sleep and endless thinking.",
  "A close cousin swore by him, so I finally tried.",
  "I decided to contact him after two other so-called readers wasted my time.",
  "It took me a while to write the first message. I wasn't sure what to say.",
  "I contacted him one afternoon just to get it out of my system.",
  "Even though I don't post reviews often, this one had to be written.",
  "I heard about him from a client of mine who kept mentioning results.",
  "I messaged him during a rough week at work.",
  "I only decided to reach out after seeing him respond to people personally.",
  "I'd been trying to solve everything on my own for years.",
  "I sent him a message right after a hard call with {member}.",
  "It took me two whole days to actually hit send on that first message.",
  "I stumbled onto his page late one night and something told me to bookmark it.",
  "I finally reached out after ignoring the idea for almost a year.",
  "I contacted him after a long silence with my family had started to hurt too much.",
  "It was my aunt who eventually convinced me to give it a shot.",
  "I had lost hope of finding someone reliable until a friend mentioned him.",
  "I messaged him from {city} between shifts, half doubting anything would come of it.",
  "I chose to reach out during a moment when nothing else was working.",
  "I decided to contact him after seeing him take his time answering questions online.",
  "The truth is, I wrote to him mostly to prove myself wrong.",
  "I reached out on a whim, and I'm glad I did.",
  "I contacted him after a long back-and-forth with myself about whether it was worth it.",
  "I wrote to him from {city} during a stretch of really difficult weeks.",
  "I had been avoiding this kind of consult for years before finally deciding.",
  "It was after a rough family dinner that I finally decided to text.",
  "I typed the first message three times before finally sending it.",
];

export const MIDDLES_EN = [
  "He let me speak first, and only stepped in once I had emptied my head.",
  "He didn't interrupt me once during the first fifteen minutes.",
  "He paused for a moment before starting, which told me he was actually reading.",
  "He named someone by their first name without me ever mentioning them.",
  "He mentioned something from my childhood that no one outside my house should know.",
  "He was straightforward, without any mystical show. Just a real conversation.",
  "He asked me for specific details, not vague questions.",
  "He didn't push extra services or add hidden costs.",
  "He gave me a couple of practical instructions and left the choice to me.",
  "At one point he stopped and asked if I was ok, because my voice had changed.",
  "He was honest about the difficult parts, not just what I wanted to hear.",
  "He spoke about {member} with respect, without pointing fingers.",
  "He didn't promise a fairy tale. He said it also depended on me.",
  "He walked me through what he saw step by step so I could keep up.",
  "He asked me a couple of questions I kept thinking about for a week.",
  "He picked up on a third person involved without me hinting anything about it.",
  "He gave me a date range as a reference, not as a pressure tactic.",
  "He was clear about what he could and could not do.",
  "He talked less about himself and more about my situation, which I appreciated.",
  "He mentioned a very specific object I had at home, which was impossible to guess.",
  "He noticed I was distracted and asked me to breathe before we continued.",
  "He didn't rush me even though the call ran longer than expected.",
  "He told me things only my sister used to know.",
  "He gave me an example that finally made me understand what was happening.",
  "He clearly knew the name of a key person in my life.",
  "He described a location in my house with details he shouldn't have known.",
  "He asked me a follow-up question that made the whole picture click.",
  "He kept his composure even when I got emotional in the middle of the call.",
  "He explained the difference between what he could do and what depended on me.",
  "He was careful with his words when he spoke about {member}.",
  "He gave me a couple of quiet moments to process what he had just said.",
];

export const REACTIONS_EN = [
  "I hung up calmer than I had felt in months.",
  "He didn't fix everything, but I ended the call with my head straight.",
  "It helped me more than I expected, honestly.",
  "I told {member} about it later and she agreed it was worth doing.",
  "I went to bed thinking about several things, but at peace.",
  "I ended with a plan, and to me that alone was worth the price.",
  "I realized I needed to hear things I had been avoiding.",
  "I sat in silence for a while after the call ended.",
  "I took notes during the call and re-read them several times that week.",
  "I felt actually listened to, which was what I really needed.",
  "The next day I woke up with clarity I hadn't had in a long time.",
  "It wasn't a fireworks show, it was an honest conversation with someone serious.",
  "I finished feeling that somebody was finally taking me seriously.",
  "The first thing I did after hanging up was breathe deeply for the first time in weeks.",
  "It took me a few days to sit with everything he told me.",
  "What I appreciated most was that he never tried to sell me anything on top.",
  "I ended up doing things that had been pending for months.",
  "The price felt fair for what I took away.",
  "I didn't leave euphoric, I left centered.",
  "I ended up thinking how to describe this without sounding dramatic, and settled for this review.",
  "I found myself smiling for no obvious reason on my way home.",
  "For the first time in a while, I actually knew what to do next.",
  "I stopped second-guessing my own judgment after that talk.",
  "I called {member} that same evening to share what I could.",
  "The rest of my day felt lighter, though I couldn't quite say why.",
  "I journaled about it that night, which I hadn't done in years.",
  "It gave me the push I had been quietly waiting for.",
  "I don't think I've felt that heard in a professional setting before.",
  "It didn't answer every question, but it settled the ones that mattered.",
  "I paid the invoice with less doubt than I have paid many receipts I regret.",
  "I closed my laptop that night without the usual anxious spiral.",
  "I ended up making a couple of overdue phone calls the next morning.",
  "I felt a strange kind of gratitude on the way home.",
  "I slept the deepest I had in over a month that same night.",
  "I ordered dinner without checking my messages compulsively, for once.",
  "I told my partner about it and he said I already looked lighter.",
  "It felt like being told what I already knew, but by someone qualified to say it.",
];

export const OUTROS_EN = [
  "I would call again without hesitation.",
  "I've already passed his contact to two people I trust.",
  "I recommend him to anyone in a similar moment.",
  "I plan to come back later for another matter.",
  "He is worth more than what he charges, honestly.",
  "I now have him saved as maestro in my phone.",
  "If you're doubting, my advice is to take the step calmly.",
  "It's not a magic wand, it's an honest guide, and that is already a lot.",
  "I keep his advice as one of the more useful things I've heard this year.",
  "I'll come back when life pushes me to consult again.",
  "This review is my way of thanking him from {city}.",
  "It ended up being a turning point for my head, and I'm grateful.",
  "I'll leave this review here for anyone who is still hesitating.",
  "If it helped me, honestly, I don't see why it wouldn't help someone else.",
  "I'll take my time to say it: I would do it again.",
  "For anyone reading this from far away like me, it works remotely just fine.",
  "I'm keeping this comment short but sincere.",
];

// =================================================================
// Just-hired — split into intro + middle + outro sub-pools so the
// ~36 just-hired comments can be assembled uniquely too.
// =================================================================
export const JH_INTRO_ES = [
  "Ya hice el pago hace poco.", "Acabo de contratar.",
  "Recién me confirmaron el trabajo.", "Terminé de pagar ayer.",
  "Apenas comienza el proceso.", "Cerré el trato esta semana.",
  "Le mandé el comprobante el lunes.", "Pagué desde {city}.",
  "Contraté ayer, todavía sin resultados que reportar.",
  "Firmé el acuerdo con calma, sin sentirme empujada.",
  "Terminó el trámite hace pocos días.",
  "Recién arranco después de darle vueltas por meses.",
  "Ya está agendado.", "Pagué por Wise desde el extranjero.",
  "Me dieron cita para arrancar la semana que viene.",
  "Confirmé el trabajo esta mañana.",
  "Ya está todo cerrado y organizado.",
];
export const JH_MIDDLE_ES = [
  "El trato en la coordinación fue rápido y ordenado.",
  "Se nota que el proceso está bien montado, sin improvisación.",
  "Todo por WhatsApp fue directo y sin dar rodeos.",
  "La comunicación fue clara y a horario.",
  "Me atendieron con calma y sin presión.",
  "Cada paso me quedó claro desde el inicio.",
  "El recibo me llegó al instante, cosa que agradecí.",
  "La atención previa me generó confianza.",
  "Respondió cada pregunta que le hice sin apuros.",
  "Fue por videollamada, ordenado y sin drama.",
  "No sentí presión de venta en ningún momento.",
  "Todo se ha explicado con detalle hasta acá.",
  "Me dieron toda la información antes de pedir el pago.",
];
export const JH_OUTRO_ES = [
  "Actualizaré cuando pasen los días.",
  "Vuelvo a comentar más adelante.",
  "Después les cuento el resultado.",
  "Espero {days} días y regreso a compartir cómo me fue.",
  "Volveré a escribir con novedades.",
  "Regreso luego con detalles.",
  "Actualizo en unas semanas.",
  "Vuelvo con lo que pase en el plazo previsto.",
  "Cuando pase el plazo, comento resultados.",
  "Regreso con la reseña completa cuando termine el trabajo.",
  "Escribo otra vez cuando tenga algo firme que contar.",
];
export const JH_INTRO_EN = [
  "Just paid today.", "Recently booked.",
  "Just started the process yesterday.",
  "I paid from {city} on Sunday.",
  "Just closed the arrangement.",
  "Got confirmation this morning.",
  "Payment went through smoothly.",
  "Only just started.", "Signed off yesterday.",
];
export const JH_MIDDLE_EN = [
  "Coordination has been quick and clean.",
  "The whole process felt organized and professional.",
  "Communication has been direct, no games.",
  "The receipt came in right away, which was a nice touch.",
  "Every step was explained clearly with no surprises.",
  "Whole process felt calm and unrushed.",
  "Coordination was clear.",
  "The attention and clarity so far have been exactly what I hoped.",
];
export const JH_OUTRO_EN = [
  "I'll be back with results soon.",
  "Will update after the wait.",
  "Will share results later.",
  "Will update in {days} days.",
  "Will follow up later.",
  "I'll write again once the wait is done.",
  "Coming back with the outcome.",
];

// Legacy exports kept for backwards compatibility (not used any more).
export const TPL_JUST_HIRED_ES = [];
export const TPL_JUST_HIRED_EN = [];

// =================================================================
// Category anchors — inject one sentence tying the comment to the
// specific service tag rendered as chip.
// =================================================================
export const CAT_ANCHORS_ES = {
  reading: [
    "La lectura fue clarísima, cada carta cayó en su lugar.",
    "Me leyó como libro abierto sin preguntarme casi nada.",
    "Cada detalle de la lectura se cumplió más o menos en las fechas que me dio.",
    "Nombres, fechas y situaciones salieron durante la lectura y coincidieron después.",
    "En la lectura me destapó cosas que llevaba guardadas años.",
    "La forma en que interpretó las cartas me pareció muy cuidada.",
    "En la lectura me habló de {member} sin haberla nombrado yo.",
    "Salí de la lectura con más claridad que dudas.",
  ],
  hechiceria: [
    "El trabajo se fue sintiendo con los días, sin apuros.",
    "Empecé a notar cambios pequeños durante el proceso y luego se hicieron más claros.",
    "Cuando terminó el trabajo el ambiente en casa se aflojó de a poco.",
    "Fue un proceso, no una promesa exprés, y por eso me pareció serio.",
    "El trabajo lo sentí acompañado paso a paso, no me dejaron sola.",
    "Al final del trabajo mi vida no explotó de felicidad, pero sí se ordenó.",
    "Los efectos del trabajo no fueron mágicos de un día para otro, fueron sostenidos.",
    "Después del trabajo cambiaron cosas concretas, no sólo sensaciones.",
  ],
  pacto: [
    "Desde el pacto siento que mi vida tiene otra dirección.",
    "El pacto fue un paso importante, tomado con conciencia.",
    "No me arrepiento del paso que di, con toda la seriedad que tuvo.",
    "Con el pacto se ordenaron cosas que llevaban años trabadas.",
    "Fue una decisión personal grande, y hoy la sostengo con calma.",
  ],
};

export const CAT_ANCHORS_EN = {
  reading: [
    "The reading was clear, with cards landing where they made sense.",
    "He read me like an open book without needing much from me.",
    "The dates he mentioned in the reading matched what actually happened.",
    "Names and situations came up during the reading and turned out to be real.",
    "I left the reading with more clarity than questions.",
  ],
  hechiceria: [
    "The work unfolded gradually, without pressure.",
    "I noticed small changes during the process and they built up over time.",
    "By the end of the work the situation at home had calmed down.",
    "It was a process, not a quick promise, and that felt trustworthy.",
    "After the work, concrete things changed, not just feelings.",
  ],
  pacto: [
    "Since the pact I feel my life has a different direction.",
    "The pact was an important step, taken with awareness.",
    "I don't regret the step, taken with all the seriousness it deserved.",
  ],
};

// =================================================================
// System / website / process praises — appended sometimes
// =================================================================
export const SYSTEM_PRAISE_ES = [
  "El proceso por la página fue claro y ordenado.",
  "La página web se ve profesional, se nota el trabajo detrás.",
  "La coordinación por WhatsApp fue rápida y sin enredos.",
  "Me llegó el recibo al momento, sin tener que pedirlo.",
  "El sistema de citas es fácil de usar, incluso para gente poco tecnológica.",
  "Me atendieron con puntualidad, sin dejarme esperando.",
  "La página tiene todo bien explicado, sin letra chiquita.",
  "Cada paso me llegaba por notificación, cosa que agradezco.",
  "El sitio funciona bien desde el celular, cosa poco común.",
  "Todo se pudo hacer a distancia, muy cómodo.",
];

export const SYSTEM_PRAISE_EN = [
  "The whole process on the site was clear and organized.",
  "Coordination via WhatsApp was quick, no back-and-forth.",
  "The booking system was easy to use, even for a person like me.",
  "I got the receipt right after paying, no need to ask.",
  "Everything could be done remotely, which was convenient.",
];

// Small sober suffixes — replace old emoji-heavy ones
export const SUFFIX_ES = [
  "", "", "", "", "", "", "", " 🙏",
];
export const SUFFIX_EN = [
  "", "", "", "", "", "",
];

// Typo maps: kept for occasional realism (very low probability now).
export const TYPO_MAP_ES = [
  ["que", "ke"], ["también", "tambien"], ["más", "mas"], ["está", "esta"],
  ["así", "asi"], ["después", "despues"], ["porque", "porq"],
  ["increíble", "increible"], ["exactamente", "exactamnte"], ["gracias", "graias"],
];
export const TYPO_MAP_EN = [
  ["really", "rly"], ["because", "bc"],
  ["thank you", "ty"], ["thanks", "thx"], ["everything", "everythn"],
];
